// Per-unit AI price comparison for the inventory table's "AI Price" column:
// the unit's asking price (Best_Sell_Price) against the AI price model
// (PredictedPrice), coloured on the same scale as the lot value tile.
import { GAP_THRESHOLDS, gapDirection, formatPctSigned } from './lotValue';
import { formatCurrency } from './formatting';

// Human wording per state, used in the accessible label.
export const AI_PRICE_STATES = Object.freeze({
    inline: 'in line with AI',
    above: 'above AI',
    wellAbove: 'well above AI',
    below: 'below AI',
    review: 'Flagged for review: check the unit before acting',
    none: 'No AI price for this unit',
});

// Positive, finite number or null. Strings, 0, negatives, NaN, Infinity → null.
const positivePrice = (value) =>
    (typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null);

// asking ÷ AI price − 1 as a fraction; null unless both prices are valid.
export const aiPriceGap = (unit) => {
    const ai = positivePrice(unit?.PredictedPrice);
    const asking = positivePrice(unit?.Best_Sell_Price);
    if (ai === null || asking === null) return null;
    return asking / ai - 1;
};

// `thresholds`: the dealership's gap scale (fractions); today's by default.
export const aiPriceState = (unit, thresholds = GAP_THRESHOLDS) => {
    const gap = aiPriceGap(unit);
    if (gap === null) return 'none';
    if (unit.PriceStatus === 'Review') return 'review';
    return gapDirection(gap, thresholds);
};

// P10–P90 range, or null when either end is missing/invalid or low > high.
export const aiPriceRange = (unit) => {
    const low = positivePrice(unit?.PriceLow);
    const high = positivePrice(unit?.PriceHigh);
    if (low === null || high === null || low > high) return null;
    return { low, high };
};

// The range to show next to the AI price: only when it supports the estimate
// (not for Review units, whose output is capped, and not when the AI price
// falls outside it). Null otherwise. Shared by the table's label and the unit
// page's hero ("likely range").
export const consistentAiRange = (unit) => {
    const ai = positivePrice(unit?.PredictedPrice);
    const range = aiPriceRange(unit);
    if (ai === null || !range || unit.PriceStatus === 'Review') return null;
    return ai >= range.low && ai <= range.high ? range : null;
};

export const aiPriceChipText = (unit, thresholds = GAP_THRESHOLDS) => {
    const state = aiPriceState(unit, thresholds);
    if (state === 'none') return '—';
    if (state === 'review') return 'Review';
    return formatPctSigned(aiPriceGap(unit));
};

// Full-sentence text alternative, so the cell never relies on colour alone.
export const aiPriceLabel = (unit, thresholds = GAP_THRESHOLDS) => {
    const ai = positivePrice(unit?.PredictedPrice);
    if (ai === null) return AI_PRICE_STATES.none;

    // Show the range only when it supports the estimate (consistentAiRange).
    const range = consistentAiRange(unit);
    const source = unit.PriceStatus === 'Comparable-based' ? ', comparable-based' : '';
    const rangeText = range ? ` (range ${formatCurrency(range.low)}–${formatCurrency(range.high)})` : '';
    const estimate = `AI estimate ${formatCurrency(ai)}${source}${rangeText}.`;

    const gap = aiPriceGap(unit);
    if (gap === null) return `${estimate} No asking price to compare`;

    const asking = `Asking ${formatCurrency(unit.Best_Sell_Price)}, ${formatPctSigned(gap)} vs AI`;
    const state = aiPriceState(unit, thresholds);
    if (state === 'review') return `${estimate} ${asking}. ${AI_PRICE_STATES.review}`;
    return `${estimate} ${asking}, ${AI_PRICE_STATES[state]}`;
};

// Short label + token colour per state, for the unit detail pane's price
// cards, so they read exactly like the table chip.
const GAP_DISPLAY = Object.freeze({
    inline: { label: 'In line with AI', color: 'var(--sag-green-dark)' },
    above: { label: 'Above AI', color: 'var(--is-amber)' },
    wellAbove: { label: 'Well above AI', color: 'var(--is-red)' },
    below: { label: 'Below AI', color: 'var(--is-teal)' },
    review: { label: 'Review', color: 'var(--is-grey)' },
});

// { state, pctText, label, color } or null when there is nothing to compare.
export const aiPriceGapDisplay = (unit, thresholds = GAP_THRESHOLDS) => {
    const state = aiPriceState(unit, thresholds);
    if (state === 'none') return null;
    return { state, pctText: formatPctSigned(aiPriceGap(unit)), ...GAP_DISPLAY[state] };
};

// Comparator on the gap; units without a gap sort last in BOTH directions.
export const compareByAiPriceGap = (direction) => (a, b) => {
    const ag = aiPriceGap(a);
    const bg = aiPriceGap(b);
    if (ag === null) return bg === null ? 0 : 1;
    if (bg === null) return -1;
    return direction === 'asc' ? ag - bg : bg - ag;
};
